<template>
  <div v-if="showPopup" class="overlay" role="presentation">
    <div
      class="popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-prompt-title"
      aria-describedby="account-prompt-copy"
    >
      <button class="close-btn" type="button" aria-label="Close account prompt" @click="closePopup">
        &times;
      </button>

      <div class="popup-hero">
        <span class="popup-kicker">
          <i class="fa fa-shield-alt" aria-hidden="true"></i>
          Free intelligence preview
        </span>
        <h1 id="account-prompt-title">See the story behind your feed.</h1>
        <p id="account-prompt-copy">
          Create a free account for the Daily Cut, a sharper read on what is moving,
          what your side may be missing, and how coverage is trying to make you feel.
        </p>
      </div>

      <div class="value-grid" aria-label="Free account intelligence tools">
        <span class="value-pill">
          <i class="fa fa-chart-line" aria-hidden="true"></i>
          <strong>Narrative movement</strong>
          <small>See which stories are gaining momentum.</small>
        </span>
        <span class="value-pill">
          <i class="fa fa-eye-slash" aria-hidden="true"></i>
          <strong>Blindspots</strong>
          <small>Catch stories one side is barely seeing.</small>
        </span>
        <span class="value-pill">
          <i class="fa fa-heart-pulse" aria-hidden="true"></i>
          <strong>Sentiment gap</strong>
          <small>Track how coverage is shaping emotion.</small>
        </span>
        <span class="value-pill">
          <i class="fa fa-scale-balanced" aria-hidden="true"></i>
          <strong>Polarization</strong>
          <small>Compare left, center, and right framing.</small>
        </span>
        <span class="value-pill value-pill--accent">
          <i class="fa fa-crosshairs" aria-hidden="true"></i>
          <strong>Under Sniper</strong>
          <small>Published picks are available on Limited and Full.</small>
        </span>
      </div>

      <div class="popup-actions">
        <a class="primary-cta" href="/register" aria-label="Create a free account and get the Daily Cut">
          Get the Daily Cut Free
          <i class="fa fa-arrow-right" aria-hidden="true"></i>
        </a>
        <a class="secondary-cta" href="/login" aria-label="Log in to your BarbershopTalk account">
          Already have an account? Log in
        </a>
      </div>

      <p class="trust-note">
        Free to start. No credit card. Built for readers who want every side and the emotional frame behind it.
        <span>The conversation starts here.</span>
      </p>
    </div>
  </div>
</template>

<script>
import { shouldSuppressNotLoggedIn } from '@/composables/useTutorialProgress';

export default {
  name: "notLoggedIn",
  data() {
    return {
      showPopup: false,
      popupTimer: null
    };
  },
  mounted() {
    if (shouldSuppressNotLoggedIn()) return;
    if (sessionStorage.getItem('notLoggedInPromptSeen') === 'true') return;

    this.popupTimer = setTimeout(() => {
      if (shouldSuppressNotLoggedIn()) return;
      this.showPopup = true;
      sessionStorage.setItem('notLoggedInPromptSeen', 'true');
    }, 10000);

    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    clearTimeout(this.popupTimer);
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    closePopup() {
      this.showPopup = false;
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
    }
  }
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3200;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(48, 136, 212, 0.24), transparent 34%),
    radial-gradient(circle at bottom right, rgba(221, 52, 52, 0.18), transparent 30%),
    rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(10px);
}

.popup {
  position: relative;
  overflow: hidden;
  overflow-y: auto;
  width: min(100%, 560px);
  max-height: calc(100vh - 36px);
  max-height: calc(100dvh - 36px);
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 28px;
  background: #ffffff;
  box-shadow: 0 28px 90px rgba(2, 6, 23, 0.36);
  text-align: left;
  overscroll-behavior: contain;
  /* hide scrollbar across browsers */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.popup::-webkit-scrollbar {
  display: none;
}

.popup::before {
  position: absolute;
  inset: 0 0 auto;
  height: 8px;
  background: linear-gradient(90deg, #0b7ee3, #dd3434);
  content: '';
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #eef2f7;
  color: #0f172a;
  cursor: pointer;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  transition: background 160ms ease, transform 160ms ease;
}

.close-btn:hover {
  background: #e2e8f0;
  transform: scale(1.04);
}

.popup-hero {
  padding: 36px 32px 18px;
  background:
    radial-gradient(circle at top right, rgba(11, 126, 227, 0.16), transparent 34%),
    linear-gradient(135deg, #ffffff, #f8fbff);
}

.popup-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 14px;
  padding: 7px 10px;
  border: 1px solid rgba(11, 126, 227, 0.14);
  border-radius: 999px;
  background: rgba(239, 246, 255, 0.78);
  color: #0b65c2;
  font-size: 0.74rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.popup-kicker i {
  color: #dd3434;
}

.popup h1 {
  max-width: 460px;
  margin: 0 0 12px;
  color: #0f172a;
  font-size: clamp(1.6rem, 5vw, 2.25rem);
  font-weight: 950;
  letter-spacing: -0.045em;
  line-height: 1.05;
}

.popup p {
  margin: 0;
}

.popup-hero p {
  max-width: 470px;
  color: #475569;
  font-size: 1rem;
  font-weight: 760;
  line-height: 1.48;
}

.value-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 18px 32px 6px;
}

.value-pill {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
  min-height: 112px;
  grid-column: span 2;
  padding: 12px;
  border: 1px solid rgba(11, 126, 227, 0.12);
  border-radius: 16px;
  background:
    linear-gradient(135deg, rgba(248, 251, 255, 0.98), rgba(255, 255, 255, 0.98));
  color: #1e293b;
  line-height: 1.2;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.045);
}

.value-pill--accent {
  grid-column: span 4;
  border-color: rgba(221, 52, 52, 0.16);
  background:
    linear-gradient(135deg, rgba(255, 245, 245, 0.98), rgba(255, 255, 255, 0.98));
}

.value-grid i {
  color: #0b7ee3;
  font-size: 1rem;
}

.value-pill--accent i {
  color: #dd3434;
}

.value-pill strong {
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 950;
}

.value-pill small {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 760;
  line-height: 1.35;
}

.popup-actions {
  display: grid;
  gap: 10px;
  padding: 18px 32px 12px;
}

.primary-cta,
.secondary-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 950;
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.primary-cta {
  min-height: 48px;
  padding: 13px 16px;
  background: linear-gradient(135deg, #0b7ee3, #0053ad);
  color: #ffffff;
  box-shadow: 0 16px 30px rgba(11, 126, 227, 0.26);
  font-size: 0.96rem;
}

.primary-cta:hover {
  color: #ffffff;
  box-shadow: 0 18px 34px rgba(11, 126, 227, 0.32);
  transform: translateY(-1px);
}

.secondary-cta {
  min-height: 42px;
  padding: 11px 14px;
  background: #eef2f7;
  color: #0f172a;
  font-size: 0.88rem;
}

.secondary-cta:hover {
  background: #e2e8f0;
}

.trust-note {
  padding: 0 32px 28px;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 750;
  line-height: 1.4;
  text-align: center;
}

.trust-note span {
  display: block;
  margin-top: 6px;
  color: #0f172a;
  font-weight: 900;
  letter-spacing: 0.03em;
}

@media (min-width: 700px) {
  .popup {
    width: min(100%, 720px);
    max-height: calc(100dvh - 48px);
  }

  .value-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    padding: 20px 40px 10px;
  }

  .popup-hero {
    padding: 40px 40px 20px;
  }

  .popup-actions,
  .trust-note {
    padding-right: 40px;
    padding-left: 40px;
  }
}

@media (max-width: 560px) {
  .overlay {
    align-items: flex-end;
    padding: 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .popup {
    width: 100%;
    max-height: 92dvh;
    overflow-y: auto;
    border-radius: 24px 24px 0 0;
    box-shadow: 0 18px 60px rgba(2, 6, 23, 0.36);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .popup::-webkit-scrollbar {
    display: none;
  }

  .popup::before {
    height: 6px;
  }

  .close-btn {
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    background: rgba(238, 242, 247, 0.94);
    font-size: 24px;
  }

  .popup-hero,
  .value-grid,
  .popup-actions,
  .trust-note {
    padding-right: 20px;
    padding-left: 20px;
  }

  .popup-hero {
    padding-top: 28px;
    padding-bottom: 14px;
  }

  .popup-kicker {
    max-width: calc(100% - 48px);
    margin-bottom: 12px;
    padding: 6px 9px;
    font-size: 0.68rem;
    letter-spacing: 0.07em;
  }

  .popup h1 {
    max-width: 310px;
    margin-bottom: 10px;
    font-size: clamp(1.7rem, 9vw, 2.05rem);
    line-height: 1.02;
    letter-spacing: -0.05em;
  }

  .popup-hero p {
    max-width: 100%;
    font-size: 0.93rem;
    line-height: 1.42;
  }

  .value-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    padding-top: 12px;
    padding-bottom: 4px;
  }

  .value-pill {
    display: grid;
    grid-template-columns: 28px 1fr;
    align-items: center;
    column-gap: 10px;
    row-gap: 2px;
    min-height: 0;
    grid-column: auto;
    padding: 10px 12px;
    border-radius: 14px;
  }

  .value-grid i {
    grid-row: span 2;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(11, 126, 227, 0.1);
    font-size: 0.84rem;
  }

  .value-pill--accent i {
    background: rgba(221, 52, 52, 0.1);
  }

  .value-pill strong {
    font-size: 0.78rem;
  }

  .value-pill small {
    font-size: 0.7rem;
    line-height: 1.28;
  }

  .popup-actions {
    gap: 8px;
    padding-top: 14px;
    padding-bottom: 10px;
  }

  .primary-cta,
  .secondary-cta {
    border-radius: 14px;
  }

  .primary-cta {
    min-height: 50px;
    font-size: 0.94rem;
  }

  .secondary-cta {
    min-height: 44px;
    font-size: 0.84rem;
  }

  .trust-note {
    padding-bottom: max(22px, env(safe-area-inset-bottom));
    font-size: 0.74rem;
    line-height: 1.34;
  }
}

@media (max-width: 380px) {
  .overlay {
    padding: 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .popup-hero,
  .value-grid,
  .popup-actions,
  .trust-note {
    padding-right: 16px;
    padding-left: 16px;
  }

  .popup-hero {
    padding-top: 24px;
  }

  .popup h1 {
    font-size: 1.62rem;
  }

  .popup-hero p {
    font-size: 0.88rem;
  }

  .value-grid {
    gap: 7px;
  }

  .value-pill {
    padding: 9px 10px;
  }
}
</style>